// src/receptionist/components/RenameMerchantDialog.tsx
//
// The pencil beside the business name in the sidebar, as Oura's (BusinessDetail
// RenameDialog): the name only. It is the same field as Business profile's
// "Business name", saved the same way: the config is read fresh, only the name
// changes, and the PUT sends the rest as the server has it, so nothing another
// screen saved in the meantime is overwritten. The new name is announced, so
// the sidebar follows at once and any open screen saves it rather than the old.

import { useState } from "react";
import { Loader2Icon } from "lucide-react";
import { getMerchant, saveMerchant } from "@service/receptionist";
import { useEscapeLayer } from "@oura/ui";
import { Button } from "./kit";
import { announceMerchantName } from "../merchantState";

/** The column is VARCHAR(120); said here rather than by a failed save. */
const NAME_MAX = 120;

export function RenameMerchantDialog({
    merchantId,
    name,
    onClose,
}: {
    merchantId: string;
    name: string;
    onClose: () => void;
}) {
    const [value, setValue] = useState(name);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const trimmed = value.trim();
    const unchanged = trimmed === name.trim();
    useEscapeLayer(onClose, !saving);

    const save = async () => {
        if (!trimmed || unchanged || saving) return;
        setSaving(true);
        setError(null);
        const fresh = await getMerchant(merchantId);
        if (!fresh.ok) {
            setSaving(false);
            setError(fresh.kind === "notFound" ? "This business isn't available to you." : fresh.message);
            return;
        }
        const result = await saveMerchant(merchantId, { ...fresh.merchant, name: trimmed });
        setSaving(false);
        if (!result.ok) {
            setError(result.message);
            return;
        }
        announceMerchantName(merchantId, result.merchant.name);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={saving ? undefined : onClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="rcp-rename-title"
                className="absolute left-1/2 top-1/2 w-[calc(100%-32px)] max-w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
            >
                <h2 id="rcp-rename-title" className="my-0 text-lg font-semibold leading-snug text-gray-900">Business name</h2>
                <p className="my-0 mt-1 text-sm text-gray-500">
                    The name your customers see in replies and in your entry link's greeting. Your WhatsApp number does
                    not change.
                </p>
                <label className="mt-4 block">
                    <span className="mb-1 block text-xs font-medium text-gray-700">Business name</span>
                    <input
                        autoFocus
                        value={value}
                        maxLength={NAME_MAX}
                        onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
                        className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-blue-500"
                    />
                </label>
                {error && <p className="my-0 mt-2 text-xs leading-relaxed text-rose-700">{error}</p>}
                <div className="mt-5 flex justify-end gap-2">
                    <Button variant="outline" size="sm" disabled={saving} onClick={onClose}>Cancel</Button>
                    <Button size="sm" disabled={!trimmed || unchanged || saving} onClick={() => void save()}>
                        {saving ? <><Loader2Icon className="h-3.5 w-3.5 animate-spin" />Saving…</> : "Save"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
